import { useHighlights } from '@fs/zion-image-viewer'
import { useQueryParams, useLocation } from '@fs/zion-router'
import { useUser } from '@fs/zion-user'
import React, { createContext, useContext, useEffect, useReducer, useRef, useMemo } from 'react'
import { getGroupId, getGroupInfo, getImageApids, getNaturalChildren } from '../../../shared/services/rms/rmsService'
import { getImagesFromApidList } from '../../../shared/utils/getImagesFromApidList'
import {
  useSharedBrowserDispatchContext,
  useSharedBrowserStateContext,
} from '../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import reducer, { defaultInitialState } from './R2UiReducer'
import { errorCollector } from '../../../shared/utils/utils'

export const R2StateContext = createContext(null)
export const R2DispatchContext = createContext(null)
export const R2HighlightsContext = createContext(null)

export default function R2ContextProvider({ children, initialState, validSession }) {
  const { query } = useQueryParams()
  const [state, dispatch] = useReducer(reducer, {
    ...(initialState || defaultInitialState),
    // Set initial view from URL on mount
    initialView: query?.view || 'explore',
    // Current view always reflects the URL
    currentView: query?.view || 'explore',
  })

  useR2GroupData({ r2Dispatch: dispatch, validSession }) // Runs when groupId param changes

  return (
    <R2StateContext.Provider value={state}>
      <R2DispatchContext.Provider value={dispatch}>{children}</R2DispatchContext.Provider>
    </R2StateContext.Provider>
  )
}

export const R2HighlightsProvider = ({ children }) => {
  const [displayHighlights, displayHighlightsApi] = useHighlights()
  const [splitScreenDisplayHighlights, splitScreenDisplayHighlightsApi] = useHighlights()
  const [recordDisplayHighlights, recordDisplayHighlightsApi] = useHighlights()
  const [splitScreenRecordDisplayHighlights, splitScreenRecordDisplayHighlightsApi] = useHighlights()

  const contextValue = useMemo(
    () => ({
      displayHighlights,
      displayHighlightsApi,
      splitScreenDisplayHighlights,
      splitScreenDisplayHighlightsApi,
      recordDisplayHighlights,
      recordDisplayHighlightsApi,
      splitScreenRecordDisplayHighlights,
      splitScreenRecordDisplayHighlightsApi,
    }),
    [
      displayHighlights,
      displayHighlightsApi,
      splitScreenDisplayHighlights,
      splitScreenDisplayHighlightsApi,
      recordDisplayHighlights,
      recordDisplayHighlightsApi,
      splitScreenRecordDisplayHighlights,
      splitScreenRecordDisplayHighlightsApi,
    ]
  )

  return <R2HighlightsContext.Provider value={contextValue}>{children}</R2HighlightsContext.Provider>
}

export const useR2StateContext = () => {
  const context = useContext(R2StateContext)
  if (!context) {
    throw new Error(
      'useR2StateContext must be used within a R2ContextProvider. If writing a test, use <StateProvider> from testUtils.js'
    )
  }
  return context
}

export const useR2DispatchContext = () => {
  const context = useContext(R2DispatchContext)
  if (!context) {
    throw new Error(
      'useR2DispatchContext must be used within a R2ContextProvider. If writing a test, use <StateProvider> from testUtils.js'
    )
  }
  return context
}

export const useR2Highlights = () => {
  const context = useContext(R2HighlightsContext)
  if (!context) {
    throw new Error(
      'useR2Highlights must be used within a R2HighlightsProvider. If writing a test, use <StateProvider> from testUtils.js'
    )
  }
  return context
}

function useR2GroupData({ r2Dispatch, validSession }) {
  const { userLoading } = useUser()
  const sharedBrowserDispatch = useSharedBrowserDispatchContext()
  const location = useLocation()
  const {
    query: { groupId: groupIdQueryParam, view },
  } = useQueryParams()
  const groupIdRef = useRef(null)
  const { unifiedApidList } = useSharedBrowserStateContext()

  useEffect(() => {
    let mounted = true

    if ((view === 'index' || view === 'fullText') && validSession && !userLoading) {
      /**
       * RIP Game plan (Lightyear does something similar, but with GQL):
       * - Initialize groupData, thumbnails, and unifiedApidList by initializing the groupIdRef with null to guarantee the first fetch
       * - Update groupData if the groupId in the url changes to a different groupId after the first fetch
       * - DON'T trust the groupId param. It can be made stale in any number of ways going forward, instead, calculate from the ark
       * - cache the reuqest for unifiedApidList in state
       * - use unifiedApidList to determine if the thumbnails need initialization
       */
      if (groupIdQueryParam !== groupIdRef.current || groupIdRef === null) {
        fetchR2GroupData()
      }
    }

    // Function declarations
    const unMount = () => {
      mounted = false
    }
    async function fetchR2GroupData() {
      try {
        const imageArk = location.pathname.split('/').pop()

        // Always trust the ark for the groupId on initialization to maintain support for
        // all the links that have been saved with a stale groupId param
        const groupId =
          groupIdRef.current === null || !groupIdQueryParam ? (await getGroupId(imageArk))?.[0] : groupIdQueryParam

        groupIdRef.current = groupId

        let fetchedGroupData = null

        if (validSession) {
          try {
            fetchedGroupData = await getGroupInfo(groupId)
            if (mounted) {
              r2Dispatch({ type: 'SET_GROUP_DATA', value: { ...fetchedGroupData, groupId } })
            }
          } catch (error) {
            // Capture the error but continue execution
            errorCollector(error)
          }
        }

        if (!unifiedApidList.length) {
          // IMPORTANT: This is where we determine if the group is a split group or not
          // Both calls fetch something like a list of apids
          // Only check parentId if we successfully got group data
          if (fetchedGroupData && fetchedGroupData.parentId) {
            await fetchR2ChildGroupApidLists(fetchedGroupData.parentId, groupId) // Gets child group objects that each have an array of apids in, and the id of the child group
          } else {
            // Fallback to fetchUnsplitApidList if there was an error or if there's no parentId
            await fetchUnsplitApidList(groupId) // This is NOT a split group, thus, no 'children' just a list of apids
          }
        }
      } catch (e) {
        errorCollector('Error fetching apids, which leads to thumbnails, which leads to loading the image:', e)
      } finally {
        r2Dispatch({ type: 'SET_LOADING_GROUP_DATA', value: false })
      }
    }

    // When viewing a split group, calling getImageApids as in the function defined below this one,
    // will result in a subsection of the group's apids, rather than the entire parent group.
    // To avoid making more calls than needed, we cache the entire set of child groups after concatenating them
    // into a single array.
    async function fetchR2ChildGroupApidLists(parentId, childId) {
      try {
        const childGroups = await getNaturalChildren(parentId)

        const fullApidsList = childGroups.reduce((acc, current) => [...acc, ...current.childIds], [])
        const currentItemGroupApids =
          childGroups.find((group) => {
            return group.parentId === childId
          })?.childIds ?? []
        const thumbnails = getImagesFromApidList(currentItemGroupApids)
        if (mounted) {
          r2Dispatch({
            type: 'SET_CHILD_GROUPS',
            value: childGroups,
          })
          initializeUnifiedApidListAndThumbnails(fullApidsList, thumbnails)
        }
      } catch (err) {
        console.error(err)
      }
    }

    async function fetchUnsplitApidList(groupId) {
      const apids = await getImageApids(groupId)

      const thumbnails = getImagesFromApidList(apids)
      if (mounted) {
        initializeUnifiedApidListAndThumbnails(apids, thumbnails)
      }
    }

    function initializeUnifiedApidListAndThumbnails(apids, thumbnails) {
      sharedBrowserDispatch({
        type: 'BATCH',
        value: [
          { type: 'SET_UNIFIED_APID_LIST', value: apids }, // This is either a sewn-together set of items, or a simple array of apids depending on whether the group is split
          { type: 'SET_THUMBNAILS', value: thumbnails },
        ],
      })
    }

    return unMount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLoading, groupIdQueryParam, validSession])
}
